/**
 * FfnRecordStore — live state of the 10 CFR 50.59 record a conversation is
 * driving.
 *
 * Populated from 50.59 record tool responses (any payload carrying
 * `panel_type: "ffn_record"`), so the sidebar panel mirrors the agent's
 * progress with no extra polling. The panel can also refresh explicitly
 * from GET /v1/fifty-fifty-nine/records/{id}.
 *
 * Not persisted: the record state is replayed from the dialogue's stored
 * tool responses on reload.
 */

import { create } from "zustand";

export interface FfnChallengeGrade {
  item_id: string;
  verdict: "pass" | "weak" | "fail" | "not_applicable";
  evidence: string;
  required_revision?: string | null;
}

export interface FfnRecordDetail {
  panel_type: "ffn_record";
  record_id: number;
  plant_id: number;
  plant_name?: string | null;
  title: string;
  status: string;
  activity_description: string;
  applicability?: Record<string, any> | null;
  screening?: Record<string, any> | null;
  evaluation_criteria_recorded: number[];
  evaluation_criteria_missing: number[];
  evaluation?: Record<string, any> | null;
  challenge?: {
    grades?: FfnChallengeGrade[];
    summary?: string;
    narrative?: string | null;
  } | null;
  conclusion?: string | null;
  report_artifact_id?: number | null;
  report_verification?: {
    id: number;
    status: "pending" | "complete" | "failed";
    tier?: string | null;
    counts?: Record<string, number> | null;
    summary?: string | null;
    dialogue_id?: number | null;
  } | null;
  research_log_entries?: number;
  challenge_recorded?: boolean;
  challenge_passed?: boolean;
  challenge_stale?: boolean;
  challenge_failed_items?: string[];
  challenge_weak_items?: string[];
  challenge_advisory_findings?: string[];
  challenge_summary?: string;
}

interface FfnRecordState {
  /** Dialogue the current record belongs to. */
  dialogueId: number | null;
  record: FfnRecordDetail | null;
  setRecord: (dialogueId: number, record: FfnRecordDetail) => void;
  clearForDialogue: (dialogueId: number) => void;
}

export const isFfnRecordDetail = (value: unknown): value is FfnRecordDetail =>
  value !== null &&
  typeof value === "object" &&
  (value as { panel_type?: unknown }).panel_type === "ffn_record" &&
  typeof (value as { record_id?: unknown }).record_id === "number";

export const useFfnRecordStore = create<FfnRecordState>((set) => ({
  dialogueId: null,
  record: null,
  setRecord: (dialogueId, record) => set({ dialogueId, record }),
  clearForDialogue: (dialogueId) =>
    set((state) =>
      // Only clear when switching to a different dialogue — the same
      // dialogue remounting (e.g. sidebar toggle) keeps its record.
      state.dialogueId !== null && state.dialogueId !== dialogueId
        ? { dialogueId: null, record: null }
        : state,
    ),
}));
